/**
 * Domain constants ported from the legacy PHP portal.
 *
 * Every code string here is written to, or read from, the shared `pmc_pt_calc`
 * database that the PHP back-office still runs against. They are part of the
 * interoperability contract between the two applications and must not be
 * renamed or "tidied up".
 */

// ─── Bill / payment lifecycle ────────────────────────────────────────────────

export const BILL_STATUS = {
  GENERATED: 'GENERATED',
  OPEN: 'OPEN',
  PARTIAL: 'PARTIAL',
  PAID: 'PAID',
  CANCELLED: 'CANCELLED',
} as const;
export type BillStatus = (typeof BILL_STATUS)[keyof typeof BILL_STATUS];

/** `ctz_online_payment.txn_status` — the simulated gateway lifecycle. */
export const TXN_STATUS = {
  INITIATED: 'INITIATED',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;
export type TxnStatus = (typeof TXN_STATUS)[keyof typeof TXN_STATUS];

export const PAYMENT_METHODS = ['UPI', 'CARD', 'NETBANKING'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export function isPaymentMethod(value: unknown): value is PaymentMethod {
  return typeof value === 'string' && (PAYMENT_METHODS as readonly string[]).includes(value);
}

// ─── Document numbering prefixes (shared sequences with the back-office) ─────

export const NUMBER_PREFIX = {
  /** `PAY-YYYY-NNNNN` — trx_property_payment.payment_no */
  PAYMENT: 'PAY',
  /** `RCPT-YYYY-NNNNN` — trx_property_receipt.receipt_no */
  RECEIPT: 'RCPT',
  /** `TXN-YYYY-NNNNNN` — ctz_online_payment.txn_no */
  ONLINE_TXN: 'TXN',
  /** `SRV-YYYY-NNNNNN` — ctz_service_application.fee_txn_no */
  SERVICE_FEE: 'SRV',
} as const;

// ─── Service applications ────────────────────────────────────────────────────

export const SERVICE_CODE = {
  PT1_NEW_TAX: 'PT1_NEW_TAX',
  PT2_RETAX: 'PT2_RETAX',
} as const;

/** Services with hand-coded finalisation logic; everything else is generic. */
export const HANDCODED_SERVICE_CODES: readonly string[] = [
  SERVICE_CODE.PT1_NEW_TAX,
  SERVICE_CODE.PT2_RETAX,
];

export const APP_STATUS = {
  DRAFT: 'DRAFT',
  PENDING_PAYMENT: 'PENDING_PAYMENT',
  SUBMITTED: 'SUBMITTED',
  /**
   * Desk-verified — documents and payment checked, no problems found. Not
   * itself an approval of the *request*, just confirmation the paperwork is
   * in order; see `ctz_service_application_acceptance`. An ADMIN action
   * today; the seam for a future dedicated "Application Verifier" role — see
   * `requireRole` call sites in `admin.routes.ts`.
   */
  ACCEPTED: 'ACCEPTED',
  /**
   * An ADMIN has tagged a field inspector (`INSPECTOR_ROLE_CODE`) to
   * physically assess the property — see `ctz_service_application_assignment`.
   * Only the assigned inspector can move it on from here.
   */
  ASSIGNED: 'ASSIGNED',
  /**
   * Action identifier only — rejecting an application closes it in the same
   * step (see `updateApplicationStatus`), so `app_status` never actually
   * holds this value; it always ends up CLOSED, with the reason recorded in
   * `ctz_service_application_rejection`. Reachable from two different gates —
   * SUBMITTED (desk review found a problem) or ASSIGNED (the field visit
   * did) — kept as its own action (rather than a plain "Close") because it
   * requires a reason and drives a distinct button/dialog either way.
   */
  REJECTED: 'REJECTED',
  /**
   * Fully done — either the field inspector successfully processed the
   * request, or it was rejected at either gate. There is no separate
   * "approved, not yet closed" status: the desk review (ACCEPTED) is where
   * the request is actually vetted, so the field visit's own outcome is
   * binary and closes the application directly. The only status that frees
   * the citizen to submit a new request for the same service — see
   * `hasOpenApplication`. Whether a CLOSED application was rejected is
   * recorded separately — see `rejectReasonCode` on `ServiceApplicationDetail`.
   * Terminal.
   */
  CLOSED: 'CLOSED',
} as const;
export type AppStatus = (typeof APP_STATUS)[keyof typeof APP_STATUS];

/**
 * Actions available from an application's current status:
 *   SUBMITTED → ACCEPTED (desk review passed) or REJECTED (desk review
 *     failed) — both an ADMIN action today.
 *   ACCEPTED  → ASSIGNED (tag a field inspector) — an ADMIN action today.
 *   ASSIGNED  → CLOSED (field visit successful) or REJECTED (field visit
 *     found a problem) — the assigned inspector's action, and only theirs;
 *     see the ownership check in `admin.routes.ts`.
 * Nothing transitions *out of* REJECTED because `app_status` never actually
 * rests there.
 */
export const APP_STATUS_TRANSITIONS: Record<string, AppStatus[]> = {
  SUBMITTED: [APP_STATUS.ACCEPTED, APP_STATUS.REJECTED],
  ACCEPTED: [APP_STATUS.ASSIGNED],
  ASSIGNED: [APP_STATUS.CLOSED, APP_STATUS.REJECTED],
};

/** Standard reasons an officer can give when rejecting a service application. */
export const REJECTION_REASON = {
  INCOMPLETE_DOCS: 'INCOMPLETE_DOCS',
  ILLEGIBLE_DOCS: 'ILLEGIBLE_DOCS',
  DOCS_MISMATCH: 'DOCS_MISMATCH',
  PROPERTY_DETAILS_MISMATCH: 'PROPERTY_DETAILS_MISMATCH',
  DUPLICATE_APPLICATION: 'DUPLICATE_APPLICATION',
  NOT_ELIGIBLE: 'NOT_ELIGIBLE',
  OTHER: 'OTHER',
} as const;
export type RejectionReason = (typeof REJECTION_REASON)[keyof typeof REJECTION_REASON];

export const REJECTION_REASON_LABEL: Record<RejectionReason, string> = {
  INCOMPLETE_DOCS: 'Incomplete or missing documents',
  ILLEGIBLE_DOCS: 'Documents illegible or poor quality',
  DOCS_MISMATCH: 'Documents do not match property records',
  PROPERTY_DETAILS_MISMATCH: 'Incorrect or mismatched property details submitted',
  DUPLICATE_APPLICATION: 'Duplicate application already in process',
  NOT_ELIGIBLE: 'Not eligible for this service',
  OTHER: 'Other',
};

export const SERVICE_FEE = {
  PT1: 500.0,
  PT2: 500.0,
} as const;

/**
 * Format examples shown beside the search boxes. Both are deliberately
 * unmatchable against real data — a valid PID opens that property's dashboard,
 * and year 9999 can never be a real application year.
 */
export const SAMPLE_PID = 'P/9/9/99999999';
export const SAMPLE_APP_NO = {
  PT1: 'PT1-9999-99999',
  PT2: 'PT2-9999-99999',
} as const;

// ─── Property attributes ─────────────────────────────────────────────────────

export const USAGE_TYPES = ['RES', 'NRES', 'MIXED', 'OPEN'] as const;
export type UsageType = (typeof USAGE_TYPES)[number];

export const CONSTRUCTION_TYPES = ['RCC', 'LOAD_BEARING', 'PATRA', 'OPEN'] as const;
export type ConstructionType = (typeof CONSTRUCTION_TYPES)[number];

export const RETAX_REASONS = ['EXTENSION', 'USAGE_CHANGE'] as const;
export type RetaxReason = (typeof RETAX_REASONS)[number];

/**
 * Translation keys for each code, so labels stay bilingual.
 * These key names are taken from pt1UsageTypes() / pt1ConstructionTypes() in
 * the legacy includes/pt1_functions.php — they are not guessable, and the
 * dynamic-service wizard resolves admin-entered option LABELS back to these
 * codes by reverse lookup, so a wrong key silently breaks that mapping.
 */
export const USAGE_TYPE_LABEL_KEYS: Record<UsageType, string> = {
  RES: 'residential',
  NRES: 'non_residential',
  MIXED: 'pt1_mixed',
  OPEN: 'open_plot',
};

export const CONSTRUCTION_TYPE_LABEL_KEYS: Record<ConstructionType, string> = {
  RCC: 'pt1_ct_rcc',
  LOAD_BEARING: 'pt1_ct_lb',
  PATRA: 'pt1_ct_patra',
  OPEN: 'pt1_ct_open',
};

export const RETAX_REASON_LABEL_KEYS: Record<RetaxReason, string> = {
  EXTENSION: 'pt2_reason_ext',
  USAGE_CHANGE: 'pt2_reason_use',
};

// ─── Document uploads ────────────────────────────────────────────────────────

export const UPLOAD_MAX_MB = 10;

export const UPLOAD_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'] as const;

/** MIME type → permitted extensions. Both are checked, as the PHP app did. */
export const UPLOAD_ALLOWED_MIME: Record<string, readonly string[]> = {
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
  'application/pdf': ['pdf'],
};

export interface DocumentSlot {
  code: string;
  labelKey: string;
  required: boolean;
}

/** PT1 upload screen slots — array order is display order. */
export const PT1_DOC_SLOTS: readonly DocumentSlot[] = [
  { code: 'EXTRACT_8A', labelKey: 'pt1_doc_8a', required: false },
  { code: 'SALE_DEED', labelKey: 'pt1_doc_sale', required: true },
  { code: 'INDEX_II', labelKey: 'pt1_doc_index2', required: true },
  { code: 'NO_DUES', labelKey: 'pt1_doc_nodues', required: false },
  { code: 'PROPERTY_MAP', labelKey: 'pt1_doc_map', required: false },
  { code: 'OCCUPANCY', labelKey: 'pt1_doc_occ', required: false },
];

/** PT2 upload screen slots — BUILD_PERMIT replaces PROPERTY_MAP. */
export const PT2_DOC_SLOTS: readonly DocumentSlot[] = [
  { code: 'EXTRACT_8A', labelKey: 'pt1_doc_8a', required: false },
  { code: 'SALE_DEED', labelKey: 'pt1_doc_sale', required: true },
  { code: 'INDEX_II', labelKey: 'pt1_doc_index2', required: true },
  { code: 'BUILD_PERMIT', labelKey: 'pt2_doc_permit', required: false },
  { code: 'NO_DUES', labelKey: 'pt1_doc_nodues', required: false },
  { code: 'OCCUPANCY', labelKey: 'pt1_doc_occ', required: false },
];

// ─── Contact queries & FAQs ──────────────────────────────────────────────────

export const QUERY_STATUS = {
  NEW: 'NEW',
  RESOLVED: 'RESOLVED',
} as const;
export type QueryStatus = (typeof QUERY_STATUS)[keyof typeof QUERY_STATUS];

// ─── Admin ───────────────────────────────────────────────────────────────────

/** Full back-office access — dashboard, transactions, queue, Service Builder, and assigning applications to an inspector. */
export const ADMIN_ROLE_CODE = 'ADMIN';

/**
 * Field inspector — the `mst_user.role_code` an application gets ASSIGNED to.
 * Can sign in to this same admin-web app, but sees only their own assigned
 * applications and can only approve/reject them after the physical visit;
 * everything else (dashboard, transactions, Service Builder, assigning) stays
 * ADMIN-only. See `requireRole` in `http/adminAuth.ts`.
 */
export const INSPECTOR_ROLE_CODE = 'FIELD_INSPECTOR';

/** Every role allowed to sign in to the admin-web app at all. */
export const ADMIN_PANEL_ROLE_CODES = [ADMIN_ROLE_CODE, INSPECTOR_ROLE_CODE] as const;

// ─── UI ──────────────────────────────────────────────────────────────────────

/** Status → semantic colour, replacing the PHP `statusChip()` class map. */
export const STATUS_TONE: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
  PAID: 'success',
  SUCCESS: 'success',
  ACTIVE: 'success',
  RESOLVED: 'success',
  SUBMITTED: 'success',
  PARTIAL: 'warning',
  INITIATED: 'warning',
  PENDING_PAYMENT: 'warning',
  ASSIGNED: 'warning',
  GENERATED: 'info',
  OPEN: 'info',
  NEW: 'info',
  DRAFT: 'info',
  ACCEPTED: 'info',
  CLOSED: 'success',
  CANCELLED: 'error',
  FAILED: 'error',
  REJECTED: 'error',
};

export function statusTone(status: string): 'success' | 'warning' | 'error' | 'info' {
  return STATUS_TONE[status?.toUpperCase() ?? ''] ?? 'info';
}

export const DEFAULT_PAGE_SIZE = 10;
